import apiClient, { type PagedResponse } from '../client'
import type { VisitaDetailResponse } from '../models/response/visita'
import type {
  ScheduleResponse,
  ScheduleListItemResponse,
  ScheduleGeologistResponse,
  ScheduleDetailResponse,
  ScheduleDocumentResponse,
  GeologistOptionResponse,
  SupplierOptionResponse,
  SupplierMiningRightResponse,
  VerificationMapItemResponse,
  InformeVerificacionListItemResponse,
} from '../models/response/programacion'
import type { CreateScheduleStep1Request } from '../models/request/programacion'

const BASE_PATH = '/programaciones'

export const programacionesService = {
  async getAll(params?: {
    page?: number
    pageSize?: number
    search?: string
  }): Promise<PagedResponse<ScheduleListItemResponse>> {
    return apiClient.get<PagedResponse<ScheduleListItemResponse>>(BASE_PATH, params)
  },

  async getById(id: string): Promise<ScheduleResponse> {
    return apiClient.get<ScheduleResponse>(`${BASE_PATH}/${id}`)
  },

  async getMap(date: string): Promise<VerificationMapItemResponse[]> {
    return apiClient.get<VerificationMapItemResponse[]>(`${BASE_PATH}/map`, { date })
  },

  async createStep1(data: CreateScheduleStep1Request, pendingFiles?: { file: File; notes: string }[]): Promise<ScheduleResponse> {
    const formData = new FormData()
    if (data.id) formData.append('id', data.id)
    if (data.name) formData.append('name', data.name)
    formData.append('startDate', data.startDate)
    formData.append('endDate', data.endDate)
    formData.append('includeOcdeAudit', String(data.includeOcdeAudit))
    formData.append('ocdeAuditRequired', String(data.ocdeAuditRequired))

    if (pendingFiles && pendingFiles.length > 0) {
      const notesList = pendingFiles.map((f) => f.notes)
      formData.append('fileNotes', notesList.join('|||'))
      for (const pf of pendingFiles) {
        formData.append('files', pf.file)
      }
    }

    return apiClient.postForm<ScheduleResponse>(`${BASE_PATH}/step1`, formData)
  },

  async addGeologist(id: string, geologistId: string): Promise<ScheduleGeologistResponse> {
    return apiClient.post<ScheduleGeologistResponse>(`${BASE_PATH}/${id}/step2/geologists`, { geologistId })
  },

  async removeGeologist(id: string, junctionId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${id}/step2/geologists/${junctionId}`)
  },

  async addDetail(id: string, miningRightId: string, companyId: string, contactReference?: string): Promise<ScheduleDetailResponse> {
    return apiClient.post<ScheduleDetailResponse>(`${BASE_PATH}/${id}/step4/details`, { miningRightId, companyId, contactReference })
  },

  async removeDetail(id: string, detailId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${id}/step4/details/${detailId}`)
  },

  async updateDetailReference(id: string, detailId: string, contactReference: string): Promise<void> {
    return apiClient.put<void>(`${BASE_PATH}/${id}/details/${detailId}/contact-reference`, { contactReference })
  },

  async advanceStep(id: string, step: number): Promise<void> {
    return apiClient.put<void>(`${BASE_PATH}/${id}/advance-step/${step}`)
  },

  async finalize(id: string): Promise<void> {
    return apiClient.post<void>(`${BASE_PATH}/${id}/finalize`)
  },

  async getGeologists(params?: { search?: string; page?: number; pageSize?: number }): Promise<PagedResponse<GeologistOptionResponse>> {
    return apiClient.get<PagedResponse<GeologistOptionResponse>>(`${BASE_PATH}/geologists`, params as Record<string, string | number>)
  },

  async getSuppliers(params?: {
    search?: string
    page?: number
    pageSize?: number
  }): Promise<PagedResponse<SupplierOptionResponse>> {
    return apiClient.get<PagedResponse<SupplierOptionResponse>>(`${BASE_PATH}/suppliers`, params)
  },

  async getSupplierMiningRights(companyId: string): Promise<SupplierMiningRightResponse[]> {
    return apiClient.get<SupplierMiningRightResponse[]>(`${BASE_PATH}/suppliers/${companyId}/mining-rights`)
  },

  async uploadDocument(scheduleId: string, formData: FormData): Promise<ScheduleDocumentResponse> {
    return apiClient.postForm<ScheduleDocumentResponse>(`${BASE_PATH}/${scheduleId}/documents`, formData)
  },

  async removeDocument(scheduleId: string, documentId: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${scheduleId}/documents/${documentId}`)
  },

  async delete(id: string): Promise<void> {
    return apiClient.delete<void>(`${BASE_PATH}/${id}`)
  },

  async getVisitaDetail(scheduleId: string, detailId: string): Promise<VisitaDetailResponse> {
    return apiClient.get<VisitaDetailResponse>(`${BASE_PATH}/${scheduleId}/details/${detailId}/visita`)
  },

  async getInformes(params?: {
    page?: number
    pageSize?: number
    search?: string
    status?: number
  }): Promise<PagedResponse<InformeVerificacionListItemResponse>> {
    return apiClient.get<PagedResponse<InformeVerificacionListItemResponse>>(`${BASE_PATH}/informes`, params)
  },

  async aprobarVisita(scheduleId: string, detailId: string, comment: string): Promise<void> {
    return apiClient.post<void>(`${BASE_PATH}/${scheduleId}/details/${detailId}/aprobar`, { comment })
  },

  async rechazarVisita(scheduleId: string, detailId: string, comment: string): Promise<void> {
    return apiClient.post<void>(`${BASE_PATH}/${scheduleId}/details/${detailId}/rechazar`, { comment })
  },
}
